import React, { useCallback } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  CircleCheck,
  Eye,
  FileText,
  KeyRound,
  LayoutGrid,
  List,
  MessageSquare,
  MessageSquareWarning,
  Monitor,
  SlidersVertical,
  SquareCheckBig,
  Star,
  Tag,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import * as TestModule from 'modules/test';
import { useIsMobile } from 'hooks/use-media-query';
import { testPhase } from 'components/workspace/lifecycle';
import { computeTestReadiness } from 'pages/TestConfig/testReadiness';
import { useTestConfigSidebar } from './TestConfigSidebarContext';

interface RailItem {
  path: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const itemClass = (isActive: boolean, dimmed: boolean) =>
  cn(
    'flex h-8 items-center gap-[9px] rounded-md px-2.5 text-[13px] transition-colors',
    isActive ? 'bg-foreground font-semibold text-background' : 'font-medium text-foreground/80 hover:bg-accent hover:text-foreground',
    dimmed && !isActive && 'opacity-50',
  );

/**
 * The workspace rail: 216px on the page background with two groups, Build and
 * Run. Run pages stay reachable before activation but read at half strength
 * under a note. Below lg it's a drawer over an ink scrim.
 */
export const TestConfigSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams();
  const isMobile = useIsMobile();
  const { collapsed, close } = useTestConfigSidebar();
  const base = testId ? `/app/tests/${testId}` : '';

  const { item: test } = TestModule.Hooks.useSingle({ id: testId || '', enabled: Boolean(testId) });
  const r = computeTestReadiness(test);
  // Run pages matter once a test has been live — a finished test's results are
  // its main content — so only a draft's are dimmed.
  const dimRun = testPhase((test as { status?: string } | undefined)?.status) === 'draft';

  // In the drawer, picking a page closes it.
  const handleNavClick = useCallback(() => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) close();
  }, [close]);

  const buildItems: RailItem[] = [
    { path: '', label: t('overview'), icon: LayoutGrid, end: true },
    { path: 'questions', label: t('questions'), icon: List },
    { path: 'access', label: t('access'), icon: KeyRound },
    { path: 'settings', label: t('settings'), icon: SlidersVertical },
    { path: 'grading', label: t('grading'), icon: SquareCheckBig },
    { path: 'certificate', label: t('certificate'), icon: Star },
    { path: 'worksheet', label: t('worksheet'), icon: FileText },
  ];
  const runItems: RailItem[] = [
    { path: 'monitoring', label: t('monitor'), icon: Monitor },
    { path: 'results', label: t('results'), icon: List },
    { path: 'reviews', label: t('grade_responses'), icon: CircleCheck },
    { path: 'sheets-review', label: t('reviews'), icon: Eye },
    { path: 'statistics', label: t('statistics'), icon: BarChart3 },
    { path: 'feedback', label: t('feedback.report_title'), icon: MessageSquare },
    { path: 'question-reports', label: t('question_reports.page_title'), icon: MessageSquareWarning },
    { path: 'unused-codes', label: t('codes'), icon: Tag },
  ];

  const renderItem = (item: RailItem, dimmed = false) => (
    <NavLink
      key={item.path || 'overview'}
      to={item.path ? `${base}/${item.path}` : base}
      end={item.end}
      onClick={handleNavClick}
      className={({ isActive }) => itemClass(isActive, dimmed)}
    >
      {({ isActive }) => (
        <>
          <item.icon className={cn('h-[15px] w-[15px] shrink-0', isActive ? 'text-background' : 'text-muted-foreground')} />
          <span className="truncate">{item.label}</span>
          {item.path === 'access' && !r.hasAccess && (
            <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-warning-500" aria-hidden />
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <>
      {!collapsed && (
        <div className="fixed inset-0 z-40 bg-[rgba(17,24,39,0.5)] lg:hidden" onClick={close} aria-hidden />
      )}
      <nav
        aria-label={t('workspace_rail_label')}
        className={cn(
          'flex flex-col overflow-hidden border-r border-border bg-background transition-transform duration-300 ease-in-out',
          // Below lg: a drawer over the page.
          'fixed inset-y-0 left-0 z-50 w-[216px] shadow-lg',
          // lg+: a static column.
          'lg:static lg:z-auto lg:shrink-0 lg:translate-x-0 lg:shadow-none',
          collapsed ? '-translate-x-full' : 'translate-x-0',
        )}
      >
        <div className="flex flex-1 flex-col overflow-y-auto px-2.5 py-3 scrollbar-thin">
          {/* The phone header has no back arrow; the way out lives here. */}
          {isMobile && (
            <Link
              to="/app/tests"
              className="mb-3 flex h-11 items-center gap-[9px] rounded-md px-2.5 text-[14px] font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4 shrink-0 text-muted-foreground" />
              {t('back_to_tests')}
            </Link>
          )}
          <div className="flex flex-col gap-0.5">
            <span className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold text-muted-foreground/70">{t('rail_build')}</span>
            {buildItems.map((item) => renderItem(item))}
          </div>
          <div className="mt-4 flex flex-col gap-0.5">
            <span className="px-2.5 pb-1.5 pt-1 text-[11px] font-semibold text-muted-foreground/70">{t('rail_run')}</span>
            {runItems.map((item) => renderItem(item, dimRun))}
          </div>
          {dimRun && (
            <p className="mx-1 mt-2 text-xs leading-[1.45] text-muted-foreground">{t('rail_run_locked_note')}</p>
          )}
        </div>
      </nav>
    </>
  );
};

export default TestConfigSidebar;
